import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ProvinceEntity } from 'src/app/core/services/location/domain/entities/province.entity';
import { LocationEntity } from 'src/app/core/services/location/domain/entities/location.entity';
import { GetProvinceUseCase } from 'src/app/core/services/location/domain/usecases/get-province.usecase';
import { GetLocationsUseCase } from 'src/app/core/services/location/domain/usecases/get-locations.usecase';
import { ClaimEntity } from 'src/app/features/claims/domain/entities/claim.entity';
import { UserData } from 'src/app/features/user/domain/entities/user-data.entity';
import { GetCompanyCuilUseCase } from 'src/app/features/company/domain/usecases/get-company-colective.usecase';
import { debounceTime, distinctUntilChanged } from 'rxjs';

interface TypeOfPerson {
  name: string;
  key: string;
}

interface Localities {
  id: number;
  name: string;
}

function cuilElevenDigitsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = control.value;
    if (v == null || v === '') {
      return null;
    }
    const digits = String(v).replace(/\D/g, '');
    return digits.length === 11 ? null : { cuilInvalid: true };
  };
}

@Component({
  selector: 'app-claimed-details-form',
  templateUrl: './claimed-details-form.component.html',
  styleUrls: ['./claimed-details-form.component.css'],
  providers: [MessageService, Toast, ConfirmationService],
})
export class ClaimedDetailsFormComponent implements OnInit, OnChanges {
  visibleCancel: boolean = false;

  @Output() claimed: EventEmitter<FormGroup> = new EventEmitter();
  @Output() stepIndex: EventEmitter<number> = new EventEmitter();
  @Output() activeModal: EventEmitter<boolean> = new EventEmitter();
  @Input() userData?: UserData;
  @Input() claim?: ClaimEntity;
  @Input() activeIndex?: number;

  isEditMode: boolean = false;
  provinces: ProvinceEntity[] = [];
  localities: Localities[] = [];

  isRepresentative: boolean = false;
  companyFound: boolean = true;

  typeOfPerson: TypeOfPerson[] = [
    { name: 'Persona física', key: 'physicalPerson' },
    { name: 'Persona jurídica', key: 'legalPerson' },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private getProvince: GetProvinceUseCase,
    private getLocations: GetLocationsUseCase,
    private getCompanyUseCase: GetCompanyCuilUseCase
  ) {}

  ngOnInit(): void {
    this.loadProvinces();
    this.formClaimedDetails
      .get('claimedProvince')
      ?.valueChanges.subscribe((selectedProvince: any) => {
        this.formClaimedDetails.patchValue({ claimedCity: '' });
        if (selectedProvince && selectedProvince.id) {
          this.loadLocalities(selectedProvince.id);
          this.formClaimedDetails.patchValue({
            claimedProvince: selectedProvince.name,
          });
        }
        this.loadLocalities(selectedProvince);
      });
    this.setupCuitSearchListener();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.isRepresentative = this.userData?.rol == 'Representante gremial';

    if (changes['claim'] && changes['claim'].currentValue) {
      this.isEditMode = true;
      this.initializeFormWithClaimData();
      this.loadLocalities(
        this.formClaimedDetails.get('claimedProvince')?.value
      );
    }
  }

  formClaimedDetails: FormGroup = this.formBuilder.group({
    claimedTypeOfPerson: ['', Validators.required],
    claimedNameLastNameOrBusinessName: [
      '',
      [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/),
      ],
    ],
    claimedCuil: ['', [Validators.required, cuilElevenDigitsValidator()]],
    claimedEmail: [
      '',
      [Validators.required, Validators.email, Validators.maxLength(100)],
    ],
    claimedPhone: [
      '',
      [
        Validators.minLength(7),
        Validators.maxLength(15),
        Validators.pattern(/^[0-9]+$/),
      ],
    ],
    claimedStreet: ['', Validators.maxLength(50)],
    claimedNumber: ['', [Validators.required, Validators.maxLength(5)]],
    claimedFloorApartment: ['', [Validators.maxLength(10)]],
    claimedTower: ['', [Validators.maxLength(30)]],
    claimedPostalCode: [
      '',
      [
        Validators.required,
        Validators.maxLength(10),
        Validators.minLength(4)
      ],
    ],
    claimedNeighborhood: [''],
    claimedProvince: ['', Validators.required],
    claimedCity: ['', Validators.required],
  });

  private initializeFormWithClaimData(): void {
    if (!this.claim) return;
    if (this.isEditMode) {
      this.formClaimedDetails.patchValue({
        claimedTypeOfPerson: this.claim.claimedTypeOfPerson || '',
        claimedNameLastNameOrBusinessName:
          this.claim.claimedNameLastNameOrBusinessName || '',
        claimedCuil: this.claim.claimedCuil || '',
        claimedEmail: this.claim.claimedEmail || '',
        claimedPhone: this.claim.claimedPhone || '',
        claimedStreet: this.claim.claimedStreet || '',
        claimedNumber: this.claim.claimedNumber || '',
        claimedFloorApartment: this.claim.claimedFloorApartment || '',
        claimedTower: this.claim.claimedTower || '',
        claimedPostalCode: this.claim.claimedPostalCode || '',
        claimedNeighborhood: this.claim.claimedNeighborhood || '',
        claimedProvince: this.claim.claimedProvince || '',
        claimedCity: this.claim.claimedCity || '',
      });
    }
  }

  private isCuitInputComplete(value: unknown): boolean {
    const digits = String(value ?? '').replace(/\D/g, '');
    return digits.length === 11;
  }

  async searchCompanyByCuit(cuit: any) {
    if (!this.isRepresentative || !this.isCuitInputComplete(cuit)) {
      return;
    }
    try {
      const cuitLimpio = String(cuit ?? '').replace(/\D/g, '');
      const result = await this.getCompanyUseCase.execute(cuitLimpio);
      if (result && 'razon_social' in result) {
        this.companyFound = true;
        this.fillFormWithCompanyData(result);
      } else {
        this.companyFound = false;
        this.resetAfterCompanyNotFound(cuit);
      }
      this.formClaimedDetails.updateValueAndValidity();
    } catch (error) {
      console.error('Error al buscar empresa por CUIT:', error);
      this.companyFound = false;
      this.resetAfterCompanyNotFound(cuit);
    }
  }

  private resetAfterCompanyNotFound(cuitMasked: unknown): void {
    const cuitActual = cuitMasked;
    this.localities = [];

    this.formClaimedDetails.patchValue(
      {
        claimedNameLastNameOrBusinessName: '',
        claimedEmail: '',
        claimedPhone: '',
        claimedStreet: '',
        claimedNumber: '',
        claimedFloorApartment: '',
        claimedTower: '',
        claimedPostalCode: '',
        claimedProvince: '',
        claimedCity: '',
      },
      { emitEvent: false },
    );

    this.formClaimedDetails
      .get('claimedCuil')
      ?.setValue(cuitActual, { emitEvent: false });
    this.formClaimedDetails.updateValueAndValidity({ emitEvent: false });
  }

  setupCuitSearchListener() {
    this.formClaimedDetails
      .get('claimedCuil')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(async (value) => {
        if (!this.isRepresentative || !this.isCuitInputComplete(value)) {
          return;
        }
        await this.searchCompanyByCuit(value);
      });
  }

  fillFormWithCompanyData(companyData: any) {
    this.formClaimedDetails.patchValue({
      claimedNameLastNameOrBusinessName: companyData.razon_social || '',
      claimedEmail: companyData.email || '',
      claimedPhone: companyData.phone || '',
      claimedStreet: companyData.street || '',
      claimedNumber: companyData.number || '',
      claimedFloorApartment: companyData.floor_apartment || '',
      claimedTower: companyData.tower || '',
      claimedPostalCode: companyData.postal_code || '',
      claimedProvince: companyData.province || '',
      claimedCity: companyData.city || '',
    });
  }

  hasErrors(controlName: string, errorType: string) {
    return (
      this.formClaimedDetails.get(controlName)?.hasError(errorType) &&
      this.formClaimedDetails.get(controlName)?.touched
    );
  }

  nextStep() {
    if (this.formClaimedDetails.invalid) return;
    this.claimed.emit(this.formClaimedDetails.value);
    this.stepIndex.emit(2);
  }

  cancel() {
    this.claimed.emit(this.formClaimedDetails.value);
    this.activeModal.emit(true);
  }

  back() {
    this.stepIndex.emit(0);
  }

  async loadProvinces() {
    try {
      const result = await this.getProvince.execute();
      if (Array.isArray(result)) {
        this.provinces = result;
      }
    } catch (error) {
      console.error('Error fetching provinces', error);
    }
  }

  async loadLocalities(provinceId: string) {
    try {
      const localities = await this.getLocations.execute(provinceId);
      if (Array.isArray(localities)) {
        this.localities = this.putIdToLocalitiesList(localities);
      }
    } catch (error) {
      console.error('Error fetching localities', error);
    }
  }

  putIdToLocalitiesList(list: LocationEntity[]): Localities[] {
    let listWithId: Localities[] = [];
    for (let i = 0; i < list.length; i++) {
      let city: Localities = {
        id: i,
        name: list[i].name,
      };
      listWithId.push(city);
    }
    return listWithId;
  }
}
